// stores/useAppStore.js
import { defineStore } from 'pinia'
import { authApi } from '@/api'
import { ref, computed } from 'vue'
import { getPlanPermissions, ALL_PERMISSIONS } from '@/config/deviceFeatures'
import { BASE_STATUS_OPTIONS } from "@/utils/config";


export const useAppStore = defineStore('app', () => {
  // ===== State =====
  const currentUser = ref(localStorage.getItem('currentUser') || '')
  const currentUserInfo = ref(null)
  const currentMode = ref('sale')
  const isLoggedIn = ref(false)

  const moduleTabs = ref([
    { key: 'production', label: '🏭 生产', componentName: 'ProductionView', keepAlive: true },
    { key: 'sale', label: '📋 销售', componentName: 'SaleView', keepAlive: true },
    { key: 'bookkeeping', label: '📒 记账', componentName: 'BookkeepingView', keepAlive: true },  // ✅ 新增
    { key: 'styles', label: '📦 款式', componentName: 'StyleView', keepAlive: true },
    { key: 'materials', label: '🧵 原料', componentName: 'MaterialView', keepAlive: true },
    { key: 'processes', label: '⚙️ 工艺', componentName: 'ProcessView', keepAlive: true },
    { key: 'customer', label: '📁 客户', componentName: 'SaleView', keepAlive: true },
  ])

  // ===== Getters =====
  const activeTab = computed(() => currentMode.value)

  const userPlan = computed(() => {
    const company = currentUserInfo.value?.company
    if (!company) return 'basic'
    return company.membership_plan || 'basic'
  })

  const rawPermissions = computed(() => {
    const perms = currentUserInfo.value?.permissions || []
    if (typeof perms === 'string') {
      try { return JSON.parse(perms) } catch { return [] }
    }
    return Array.isArray(perms) ? perms : []
  })

  const planPermissions = computed(() => {
    if (currentUserInfo.value?.role === 'super_admin') {
      return ALL_PERMISSIONS
    }
    return getPlanPermissions(userPlan.value)
  })

  const userPermissions = computed(() => {
    const user = currentUserInfo.value
    const raw = rawPermissions.value

    if (user?.role === 'super_admin') return ALL_PERMISSIONS

    if (user?.role === 'admin') {
      return planPermissions.value
    }

    if (raw.includes('*')) return planPermissions.value
    return raw.filter(p => planPermissions.value.includes(p))
  })

  // ============================================================
  // ✅ 判断当前用户是否是生产人员（根据 user_type）
  // ============================================================
  const isWorker = computed(() => {
    const user = currentUserInfo.value
    if (!user) return false
    return user.user_type === 'worker'
  })

  // ============================================================
  // ✅ 动态状态选项（全局使用）
  // ============================================================
  const statusOptions = computed(() => {
    // 如果是生产人员，隐藏"已验收"和"已取消"
    if (isWorker.value) {
      const hiddenForWorker = ['已验收', '已取消']
      return BASE_STATUS_OPTIONS.filter(opt => !hiddenForWorker.includes(opt.value))
    }
    // 非生产人员显示所有状态
    return BASE_STATUS_OPTIONS
  })

  // ===== Actions =====
  function setCurrentUser(username) {
    currentUser.value = username
    localStorage.setItem('currentUser', username)
  }

  function setCurrentUserInfo(info) {
    currentUserInfo.value = info
  }

  function setMode(mode) {
    currentMode.value = mode
  }

  async function checkLoginStatus() {
    if (!currentUser.value) {
      isLoggedIn.value = false
      return false
    }

    try {
      const data = await authApi.getCurrentUser()
      if (data && data.success) {
        currentUserInfo.value = data.user
        isLoggedIn.value = true
        return true
      }
      return false
    } catch (err) {
      if (err.message === '401') {
        logout()
        return false
      }
      return false
    }
  }

  // ✅ 刷新用户信息（用于套餐变更后实时更新权限）
  async function refreshUserInfo() {
    try {
      const data = await authApi.getCurrentUser()
      if (data && data.success) {
        currentUserInfo.value = data.user
        return true
      }
      return false
    } catch (err) {
      console.error('刷新用户信息失败:', err)
      return false
    }
  }

  function logout() {
    currentUser.value = ''
    currentUserInfo.value = null
    isLoggedIn.value = false
    localStorage.removeItem('currentUser')
  }

  // ===== 权限检查 =====
  function hasPermission(perm) {
    if (!currentUserInfo.value) return false
    if (currentUserInfo.value.role === 'super_admin') return true
    return userPermissions.value.includes(perm)
  }

  function hasAnyPermission(perms) {
    if (!Array.isArray(perms) || perms.length === 0) return true
    if (currentUserInfo.value?.role === 'super_admin') return true
    return perms.some(p => hasPermission(p))
  }

  function hasAllPermissions(perms) {
    if (!Array.isArray(perms) || perms.length === 0) return true
    if (currentUserInfo.value?.role === 'super_admin') return true
    return perms.every(p => hasPermission(p))
  }

  return {
    currentUser,
    currentUserInfo,
    currentMode,
    isLoggedIn,
    moduleTabs,
    activeTab,
    userPermissions,
    userPlan,
    planPermissions,
    rawPermissions,
    // ✅ 新增
    isWorker,
    statusOptions,
    setCurrentUser,
    setCurrentUserInfo,
    setMode,
    checkLoginStatus,
    refreshUserInfo,
    logout,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  }
})